// The Academy's top bar, mirroring the nav on policenarratives.ai: logo on
// the left, a few quiet section links, then on the right a text link back
// to the app and one glowing primary button ("Log in" / "Get Started" on the
// marketing site become "Back to the app" / "Start learning"). Fixed to the
// top, translucent with a blur, and it grows a hairline border once the page
// has scrolled, exactly like the marketing nav's `.scrolled` state.
import { ArrowLeftIcon, Bars3Icon, MoonIcon, PlayIcon, SunIcon, XMarkIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "../../components/common/Logo";
import { useTheme } from "../../context/ThemeContext";
import { ALL_LESSONS } from "../catalog";
import { useAcademyProgress } from "../progress";
import { lessonPath } from "./LessonCard";

/** Height of the bar; content below offsets by the same amount (`pt-16`). */
export const ACADEMY_NAV_HEIGHT_CLASS = "h-16";

const SECTION_LINKS: { label: string; to: string }[] = [
  { label: "Courses", to: "/academy#courses" },
  { label: "What's new", to: "/academy#whats-new" },
  { label: "Sample files", to: "/academy#samples" },
];

/** "Log in"-style quiet text link. */
const quiet = "text-sm text-base-content/60 hover:text-base-content transition-colors";

const AcademyTopNav: React.FC<{
  browseOpen: boolean;
  onToggleBrowse: () => void;
}> = ({ browseOpen, onToggleBrowse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { resolvedTheme, setTheme } = useTheme();
  const { isCompleted, loading } = useAcademyProgress();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // The primary button: the first lesson until something is done, then the
  // next unfinished one.
  const started = !loading && ALL_LESSONS.some((l) => isCompleted(l.id));
  const target = ALL_LESSONS.find((l) => !isCompleted(l.id)) ?? ALL_LESSONS[0];
  const onLesson = /^\/academy\/[^/]+\/[^/]+/.test(location.pathname);

  return (
    <nav
      aria-label="Academy top navigation"
      className={cn(
        "fixed top-0 left-0 right-0 z-40 backdrop-blur-xl transition-[background-color,border-color,box-shadow] duration-300 border-b",
        scrolled ? "bg-base-100/85 border-base-300 shadow-[0_1px_8px_rgba(0,0,0,0.04)]" : "bg-base-200/70 border-transparent",
      )}
    >
      <div className={cn("max-w-[1440px] mx-auto px-6 flex items-center justify-between", ACADEMY_NAV_HEIGHT_CLASS)}>
        <div className="flex items-center gap-10 min-w-0">
          <Link to="/academy" className="shrink-0 flex items-center" aria-label="Academy home">
            <Logo className="h-7 w-auto" />
          </Link>
          <div className="hidden md:flex items-center gap-8">
            {SECTION_LINKS.map((l) => (
              <Link key={l.to} to={l.to} className={quiet}>
                {l.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-1 sm:gap-3">
          <button
            type="button"
            aria-label="Toggle dark mode"
            className="p-2 rounded-lg text-base-content/50 hover:text-base-content/80 hover:bg-base-200 transition-colors"
            onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
          >
            {resolvedTheme === "dark" ? <SunIcon className="size-5" /> : <MoonIcon className="size-5" />}
          </button>
          <Link to="/cases" className={cn(quiet, "hidden sm:inline-flex items-center gap-1.5 px-4 py-2")}>
            <ArrowLeftIcon className="size-4" /> Back to the app
          </Link>
          {target && !onLesson && (
            <button
              type="button"
              onClick={() => navigate(lessonPath(target))}
              className={cn(
                "relative inline-flex items-center gap-1.5 rounded-[10px] bg-blue-500 text-white text-sm font-semibold px-5 py-[9px]",
                "shadow-[0_0_12px_rgba(7,104,253,0.25)] hover:shadow-[0_0_16px_rgba(7,104,253,0.4)] hover:-translate-y-px",
                "transition-[transform,box-shadow] duration-200 motion-reduce:transition-none motion-reduce:hover:translate-y-0",
              )}
            >
              <PlayIcon className="size-4" />
              {started ? "Continue" : "Start learning"}
            </button>
          )}
          <button
            type="button"
            className="lg:hidden p-2 -mr-2 rounded-lg text-base-content/70 hover:bg-base-200 transition-colors"
            onClick={onToggleBrowse}
            aria-expanded={browseOpen}
            aria-controls="academy-browse"
            aria-label={browseOpen ? "Close the lesson browser" : "Browse lessons"}
          >
            {browseOpen ? <XMarkIcon className="size-5" /> : <Bars3Icon className="size-5" />}
          </button>
        </div>
      </div>
    </nav>
  );
};

export default AcademyTopNav;
